/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import * as React from "react";
import Collapsible from "react-collapsible";
import classnames from "classnames";
import { NavLink } from "react-router-dom";

import { LocalizedPolicy, LocalizedPolicyInformation, PolicyList } from "../../../../../api/legal/models/policies";
import DocumentFilterAccordionLogic, { AccordionData } from "../../../../../Logic/Legal/DocumentFilterAccordionLogic";
import { WithPropsGuard } from "../../../_common";
import styles from "./DocumentFilterAccordion.module.scss";
import "./Collapsible.scss";

interface Props {
  policies: PolicyList;
  onClickPolicy: (localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation) => void;
  activeLanguage: string;
  activeLocalizedPolicyId: string;
}

const Policy = (props: {
  name: string;
  active: boolean;
  localizedPolicy: LocalizedPolicy;
  onClickPolicy: (localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation) => void;
}) => {
  const { active, localizedPolicy, name, onClickPolicy } = props;
  if (!localizedPolicy.localeCode) return null;
  return (
    <li key={localizedPolicy.id} className={active ? styles.active : ""}>
      {active && <span>{name}</span>}
      {!active && (
        <NavLink
          onClick={() => {
            onClickPolicy(localizedPolicy);
          }}
          to={`/${localizedPolicy.id}`}
        >
          {name}
        </NavLink>
      )}
    </li>
  );
};

const Accordion = (props: {
  accordionData: AccordionData[];
  onClickPolicy: (localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation) => void;
}) => {
  const { accordionData, onClickPolicy } = props;
  return (
    <div>
      {accordionData.map((accordion) => {
        if (!accordion.policies.length) return null;
        return (
          <Collapsible
            key={accordion.namespace}
            open={!!accordion.policies && !!accordion.policies.length}
            openedClassName={classnames(styles.accordion, styles.opened)}
            className={styles.accordion}
            trigger={accordion.namespace}
          >
            <ul>
              {accordion.policies.map((policy) => (
                <Policy
                  key={policy.localizedPolicy.id}
                  onClickPolicy={onClickPolicy}
                  name={policy.name}
                  localizedPolicy={policy.localizedPolicy}
                  active={accordion.activeLocalizedPolicyId === policy.localizedPolicy.id}
                />
              ))}
            </ul>
          </Collapsible>
        );
      })}
    </div>
  );
};

const DocumentFilterAccordion = WithPropsGuard(
  class DocumentFilterAccordion extends React.Component<Props> {
    private readonly logic: DocumentFilterAccordionLogic;

    constructor(props: Props) {
      super(props);
      const { policies, activeLanguage, activeLocalizedPolicyId } = props;
      this.logic = new DocumentFilterAccordionLogic({ policies, activeLanguage, activeLocalizedPolicyId });
      this.logic.subscribe(() => this.setState({}));
    }

    componentDidMount(): void {
      this.logic.init();
    }

    render() {
      const { accordionData } = this.logic.state;
      const accordionDataArray = Object.values(accordionData);
      return (
        <div className={styles.container}>
          {accordionDataArray.length > 0 && (
            <Accordion onClickPolicy={this.props.onClickPolicy} accordionData={accordionDataArray} />
          )}
        </div>
      );
    }
  },
  (prevProps, nextProps) =>
    prevProps.policies.toString() !== nextProps.policies.toString() ||
    prevProps.activeLanguage !== nextProps.activeLanguage
);

export default DocumentFilterAccordion;
